import { type HandoverDocument, type HandoverRoom, handoverDocumentSchema } from '@sivan/shared';
import { useState } from 'react';
import { ApiError, NetworkError, apiPost } from '../api/client';
import { Modal } from '../master-data/Modal';
import { askConfirm } from '../ui/confirm';
import { Icon } from '../ui/Icon';
import type { DraftFinding } from './handover-draft';

/** The rooms this handover inspects, with their items and the findings recorded in each. */
function currentRooms(doc: HandoverDocument, findings: readonly DraftFinding[]) {
  const rooms = new Map<
    string,
    { areaId: string; area: string; items: number; findings: number }
  >();
  const roomOfItem = new Map<string, string>();
  for (const item of doc.inspection.items) {
    roomOfItem.set(item.id, item.areaId);
    const room = rooms.get(item.areaId);
    rooms.set(item.areaId, {
      areaId: item.areaId,
      area: item.area,
      items: (room?.items ?? 0) + 1,
      findings: room?.findings ?? 0,
    });
  }
  for (const f of findings) {
    const areaId =
      (f.inspectionItemId === null ? undefined : roomOfItem.get(f.inspectionItemId)) ?? f.areaId;
    const room = areaId === null ? undefined : rooms.get(areaId);
    if (room !== undefined) rooms.set(room.areaId, { ...room, findings: room.findings + 1 });
  }
  return [...rooms.values()];
}

const itemsText = (n: number) => (n === 1 ? 'פריט בדיקה אחד' : `${String(n)} פריטי בדיקה`);

function explain(error: unknown): string {
  if (error instanceof NetworkError) return 'אין חיבור לשרת. החדרים לא השתנו; נסו שוב.';
  if (error instanceof ApiError) {
    const fields = error.body['fields'] as Record<string, string> | undefined;
    if (fields?.['areaId'] === 'has_findings')
      return 'בחדר הזה רשומים ליקויים, ולכן הוא נשאר במסירה.';
    if (error.status === 409) return 'המסירה השתנתה בינתיים. רעננו אותה ונסו שוב.';
    if (error.status === 403) return 'אין לך הרשאה לשנות את החדרים במסירה.';
  }
  return 'שינוי החדרים נכשל. החדרים לא השתנו.';
}

/**
 * The rooms of this apartment, inside the handover: leave out a room the apartment does not have,
 * put it back, or add a room of the project's template that is set for other apartment types. The
 * project's template is not changed, and nothing recorded is deleted: a room left out keeps its
 * items and results, and a room with findings stays.
 */
export function RoomsDialog({
  doc,
  findings,
  dirty,
  open,
  onClose,
  onChanged,
}: {
  readonly doc: HandoverDocument;
  readonly findings: readonly DraftFinding[];
  /** Unsaved changes: the rooms change only on a saved draft. */
  readonly dirty: boolean;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onChanged: (doc: HandoverDocument) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rooms = currentRooms(doc, findings);
  const send = (areaId: string, action: 'include' | 'exclude') => {
    setBusy(areaId);
    setError(null);
    apiPost(
      `/projects/${doc.project.id}/handovers/${doc.id}/rooms`,
      { rowVersion: doc.rowVersion, areaId, action },
      handoverDocumentSchema,
    )
      .then(onChanged)
      .catch((e: unknown) => {
        setError(explain(e));
      })
      .finally(() => {
        setBusy(null);
      });
  };
  const exclude = (room: { areaId: string; area: string; items: number }) => {
    void askConfirm({
      title: `להסיר את "${room.area}" מהמסירה?`,
      message: `${itemsText(room.items)} לא ייבדקו בדירה הזו. תוצאות שכבר סומנו נשמרות, ואפשר להחזיר את החדר. תבנית הפרויקט לא משתנה.`,
      confirmLabel: 'הסרת החדר',
    }).then((yes) => {
      if (yes) send(room.areaId, 'exclude');
    });
  };
  const disabled = dirty || busy !== null;
  const list = (title: string, items: readonly HandoverRoom[], label: string, hint: string) =>
    items.length > 0 && (
      <section className="rooms-dialog__group" aria-label={title}>
        <h3>{title}</h3>
        <p className="hint">{hint}</p>
        <ul className="rooms-dialog__list">
          {items.map((r) => (
            <li key={r.areaId} className="rooms-dialog__row">
              <span>
                <b>{r.area}</b> <span className="hint">{itemsText(r.items)}</span>
              </span>
              <button
                type="button"
                className="button"
                disabled={disabled}
                aria-label={`${label}: ${r.area}`}
                onClick={() => {
                  send(r.areaId, 'include');
                }}
              >
                <Icon name="plus" /> {busy === r.areaId ? 'מוסיף…' : label}
              </button>
            </li>
          ))}
        </ul>
      </section>
    );
  return (
    <Modal title="חדרים בדירה" open={open} onClose={onClose}>
      {open && (
        <div className="rooms-dialog">
          <p className="hint">
            השינוי חל על המסירה הזו בלבד. תבנית הבדיקה של הפרויקט נשארת כפי שהיא.
          </p>
          {dirty && (
            <p className="notice notice--warning" role="status">
              יש שינויים שעדיין נשמרים. אפשר לשנות חדרים אחרי שהטיוטה נשמרה.
            </p>
          )}
          {error !== null && (
            <p className="notice notice--error" role="alert">
              {error}
            </p>
          )}
          <section className="rooms-dialog__group" aria-label="חדרים במסירה">
            <h3>חדרים במסירה</h3>
            <ul className="rooms-dialog__list">
              {rooms.map((r) => (
                <li key={r.areaId} className="rooms-dialog__row">
                  <span>
                    <b>{r.area}</b> <span className="hint">{itemsText(r.items)}</span>
                    {r.findings > 0 && (
                      <span className="hint rooms-dialog__why">
                        {' '}
                        · יש בו ליקויים, ולכן הוא נשאר
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={disabled || r.findings > 0}
                    aria-label={`הסרה מהמסירה: ${r.area}`}
                    onClick={() => {
                      exclude(r);
                    }}
                  >
                    <Icon name="minus" /> {busy === r.areaId ? 'מסיר…' : 'הסרה'}
                  </button>
                </li>
              ))}
            </ul>
          </section>
          {list(
            'חדרים שהוסרו',
            doc.inspection.excludedRooms,
            'החזרה',
            'החדרים האלה לא נבדקים בדירה הזו. החזרה מביאה אותם עם התוצאות שסומנו.',
          )}
          {list(
            'חדרים נוספים מהתבנית',
            doc.inspection.addableRooms,
            'הוספה',
            'חדרים שהוגדרו בתבנית לטיפוסי דירה אחרים.',
          )}
          <div className="form-actions">
            <button type="button" className="button button--primary" onClick={onClose}>
              סיום
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
